import { Button } from "@/components/ui/baseUi/button"
import { Separator } from "@/components/ui/baseUi/separator"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { BoxSlotSubscriber } from "@/modules/boxSlot/components/boxSlotSubscriber"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { cn } from "@/modules/lib/utils"
import type { SlotSubscriberEntity } from "@/modules/profile/subscriber/entities/slotSubscriberEntity"
import SlotEmptySVG from "@images/empty/slot_empty.svg"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import {
  BoxSessionAddSubscriberModal,
  type BoxSessionAddSubscriberModalRef,
} from "@modules/boxSlot/components/boxSessionAddSubscriberModal"
import { useBoxSlotDetails, useBoxSlotSubscribers } from "@modules/boxSlot/hooks/useBoxSlotDetails"
import { word } from "@modules/core/utils/i18n"
import { ParticipantStatus } from "@modules/profile/subscriber/subscriberTypes"
import { useCallback, useEffect, useMemo, useRef } from "react"

type BoxSlotRegistrationInfoProps = {
  boxSlotId: string
  className?: string
} & React.ComponentProps<"div">

export function BoxSlotRegistrationInfo({ boxSlotId, className, ...props }: BoxSlotRegistrationInfoProps) {
  const canAddSubscriber = useBoxStaffPermission(BoxStaffPermissions.MANAGE_SLOTS)
  const addSubscriberModalRef = useRef<BoxSessionAddSubscriberModalRef>(null)
  const loadMoreRefSubscribers = useRef<HTMLDivElement | null>(null)
  const loadMoreRefPendingSubscribers = useRef<HTMLDivElement | null>(null)
  const { data: boxSlot } = useBoxSlotDetails(boxSlotId)

  const {
    data: subscribersRegistered,
    handleNextPage: handleNextPageSubscribersRegistered,
    hasNextPage: hasNextPageSubscribersRegistered,
    totalElements: totalElementsSubscribersRegistered,
  } = useBoxSlotSubscribers({ slotId: boxSlotId, participantStatus: ParticipantStatus.REGISTERED })

  const {
    data: subscribersPending,
    handleNextPage: handleNextPageSubscribersPending,
    hasNextPage: hasNextPageSubscribersPending,
    totalElements: totalElementsSubscribersPending,
  } = useBoxSlotSubscribers({ slotId: boxSlotId, participantStatus: ParticipantStatus.PENDING })

  const renderTotalSubscribers = useMemo(() => {
    return `${totalElementsSubscribersPending + totalElementsSubscribersRegistered} / ${boxSlot.participantLimit} ${word(
      "box.slotTemplateGroup.form.participantLimit.unit",
      {
        count: totalElementsSubscribersPending + totalElementsSubscribersRegistered,
      },
    )}`
  }, [totalElementsSubscribersPending, totalElementsSubscribersRegistered, boxSlot])

  const handleIntersectionSubscribers = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPageSubscribersRegistered) {
        handleNextPageSubscribersRegistered()
      }
    },
    [handleNextPageSubscribersRegistered, hasNextPageSubscribersRegistered],
  )

  const handleIntersectionPendingSubscribers = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPageSubscribersPending) {
        handleNextPageSubscribersPending()
      }
    },
    [handleNextPageSubscribersPending, hasNextPageSubscribersPending],
  )

  const handleClickAddSubscriber = useCallback(() => {
    if (canAddSubscriber) {
      addSubscriberModalRef.current?.open()
    } else {
      toast.error(word("boxSlotRegistrationInfo.button.inviteAthleteUnauthorized"), {
        classNames: errorToastClassName(),
      })
    }
  }, [canAddSubscriber])

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersectionSubscribers, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRefSubscribers.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPageSubscribersRegistered) {
        handleNextPageSubscribersRegistered()
      }
    }

    return () => observer.disconnect()
  }, [handleIntersectionSubscribers, hasNextPageSubscribersRegistered, handleNextPageSubscribersRegistered])

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersectionPendingSubscribers, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRefPendingSubscribers.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPageSubscribersPending) {
        handleNextPageSubscribersPending()
      }
    }

    return () => observer.disconnect()
  }, [handleIntersectionPendingSubscribers, hasNextPageSubscribersPending, handleNextPageSubscribersPending])

  return (
    <div className={cn("flex w-full grow flex-col px-4 pt-4", className)} id="a" {...props}>
      <Text className="mt-2 text-textDefault-caption" fontStyle="BS">
        {renderTotalSubscribers}
      </Text>
      <div
        className="group mt-6 mb-4 ml-1 flex cursor-pointer flex-row items-center justify-start gap-4"
        onClick={() => void handleClickAddSubscriber()}
      >
        <Button variant="secondary" size="iconMd" className="group-hover:bg-(--button-secondary-bg-primary-hover)">
          <PlusIcon color="currentColor" width={14} height={14} />
        </Button>

        <Text className="ml-1 content-center text-center text-textDefault-body" fontStyle="LL">
          {word("boxSlotRegistrationInfo.button.inviteAthlete")}
        </Text>
      </div>
      <Separator className="m-0 mb-2" />

      {subscribersRegistered.length === 0 && subscribersPending.length === 0 ? (
        <EmptyTemplate />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <SlotSubscriberTemplate
              slotSubscribers={subscribersRegistered}
              boxSlotId={boxSlotId}
              participantStatus={ParticipantStatus.REGISTERED}
            />
            {hasNextPageSubscribersRegistered && (
              <div ref={loadMoreRefSubscribers} className="flex h-auto w-full items-center justify-center py-4">
                <Text fontStyle="BM">{word("global.loading")}</Text>
              </div>
            )}
          </div>
          {subscribersPending.length > 0 ? (
            <div className="flex flex-col gap-2">
              <SlotSubscriberTemplate
                slotSubscribers={subscribersPending}
                participantStatus={ParticipantStatus.PENDING}
                boxSlotId={boxSlotId}
              />
              {hasNextPageSubscribersPending && (
                <div
                  ref={loadMoreRefPendingSubscribers}
                  className="flex h-auto w-full items-center justify-center py-4"
                >
                  <Text fontStyle="BM">{word("global.loading")}</Text>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      <BoxSessionAddSubscriberModal ref={addSubscriberModalRef} slotId={boxSlotId} />
    </div>
  )
}

type SlotSubscriberTemplateProps = {
  slotSubscribers: readonly SlotSubscriberEntity[]
  participantStatus: ParticipantStatus
  boxSlotId: string
  className?: string
} & React.ComponentProps<"div">

function SlotSubscriberTemplate({
  slotSubscribers,
  participantStatus,
  boxSlotId,
  className,
  ...props
}: SlotSubscriberTemplateProps) {
  return (
    <div className={cn(`${participantStatus === ParticipantStatus.PENDING ? "" : "mb-5"}`, className)} {...props}>
      {participantStatus === ParticipantStatus.PENDING ? (
        <div>
          <Separator className="m-0 mb-2" />
          <Text fontStyle="BS" className="text-textDefault-body">
            {word("boxSlotRegistrationInfo.slotSubscriberTemplate.waitingList")}
          </Text>
        </div>
      ) : null}
      <div className={`${participantStatus === ParticipantStatus.PENDING ? "mt-3" : ""}`}>
        {slotSubscribers.map((subscriber) => (
          <BoxSlotSubscriber
            slotId={boxSlotId}
            subscriber={subscriber}
            participantStatus={participantStatus}
            key={subscriber.memberId}
          />
        ))}
      </div>
    </div>
  )
}

type EmptyTemplateProps = {
  className?: string
} & React.ComponentProps<"div">

function EmptyTemplate({ className, ...props }: EmptyTemplateProps) {
  return (
    <div className={cn("mt-16 flex h-60 flex-col", className)} {...props}>
      <div className="justify-center">
        <Text fontStyle="TL" className="mx-auto flex w-88 text-left text-textDefault-body">
          {word("boxSlotRegistrationInfo.emptyTemplate.title")}
        </Text>
        <Text fontStyle="BM" className="mx-auto mt-4 flex w-88 text-left text-textDefault-body">
          {word("boxSlotRegistrationInfo.emptyTemplate.subtitle")}
        </Text>
      </div>

      <div className="mt-2 flex flex-1 items-center justify-center text-center">
        <img src={SlotEmptySVG} alt="SlotEmptySVG" width={300} />
      </div>
    </div>
  )
}
